import React from "react";
import "../../../asset/new/css/admin-modern.css";

/**
 * LoadingSkeleton - Reusable loading skeleton components
 * Provides various skeleton types for different UI elements
 */

// Table Row Skeleton
export const TableRowSkeleton = ({ columns = 5, rows = 5 }) => {
  return (
    [...Array(rows)].map((_, rowIndex) => (
      <tr key={rowIndex}>
        {[...Array(columns)].map((_, colIndex) => (
          <td key={colIndex}>
            <div className="admin-skeleton" style={{ height: "20px", width: "100%" }}></div>
          </td>
        ))}
      </tr>
    ))
  );
};

// Card Skeleton
export const CardSkeleton = ({ count = 3 }) => {
  return (
    <div className="row">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="col-md-4 mb-3">
          <div className="admin-card">
            <div className="admin-card-body">
              <div className="admin-skeleton" style={{ height: "80px", marginBottom: "10px" }}></div>
              <div className="admin-skeleton" style={{ height: "20px", width: "60%" }}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Stats Card Skeleton
export const StatsCardSkeleton = ({ count = 4 }) => {
  return (
    <div className="admin-grid admin-grid-cols-4 admin-gap-md" style={{ marginBottom: "2rem" }}>
      {[...Array(count)].map((_, index) => (
        <div key={index} className="admin-card">
          <div className="admin-card-body">
            <div className="admin-flex admin-items-center admin-justify-between">
              <div style={{ flex: 1 }}>
                <div className="admin-skeleton" style={{ height: "20px", width: "60%", marginBottom: "10px" }}></div>
                <div className="admin-skeleton" style={{ height: "32px", width: "40%" }}></div>
              </div>
              <div className="admin-skeleton" style={{ height: "48px", width: "48px", borderRadius: "8px" }}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// List Skeleton
export const ListSkeleton = ({ items = 5 }) => {
  return (
    <div className="admin-card">
      <div className="admin-card-body">
        {[...Array(items)].map((_, index) => (
          <div key={index} style={{ marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid var(--admin-border)" }}>
            <div className="admin-skeleton" style={{ height: "20px", width: "70%", marginBottom: "8px" }}></div>
            <div className="admin-skeleton" style={{ height: "16px", width: "50%" }}></div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Form Skeleton
export const FormSkeleton = () => {
  return (
    <div className="admin-card">
      <div className="admin-card-body">
        {[...Array(5)].map((_, index) => (
          <div key={index} style={{ marginBottom: "20px" }}>
            <div className="admin-skeleton" style={{ height: "16px", width: "120px", marginBottom: "8px" }}></div>
            <div className="admin-skeleton" style={{ height: "40px", width: "100%" }}></div>
          </div>
        ))}
        <div className="admin-skeleton" style={{ height: "44px", width: "150px", borderRadius: "6px" }}></div>
      </div>
    </div>
  );
};

// Page Loading (Full Page)
export const PageLoading = () => {
  return (
    <div className="admin-loading-overlay">
      <div className="admin-loading-spinner">
        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: "1rem", color: "var(--admin-text-secondary)" }}>Loading...</p>
      </div>
    </div>
  );
};

// Inline Loading (Small)
export const InlineLoading = ({ text = "Loading..." }) => {
  return (
    <div className="admin-inline-loading">
      <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
        <span className="visually-hidden">{text}</span>
      </div>
      <span>{text}</span>
    </div>
  );
};

// Chart Skeleton
export const ChartSkeleton = ({ height = "300px" }) => {
  return (
    <div className="admin-card">
      <div className="admin-card-body">
        <div className="admin-skeleton" style={{ height: "24px", width: "180px", marginBottom: "20px" }}></div>
        <div className="admin-skeleton" style={{ height, width: "100%" }}></div>
      </div>
    </div>
  );
};

// Main LoadingSkeleton Component
const LoadingSkeleton = ({ type = "table-row", count = 5, columns = 5, rows = 5 }) => {
  switch (type) {
    case "table-row":
      return <TableRowSkeleton columns={columns} rows={count || rows} />;
    case "card":
      return <CardSkeleton count={count} />;
    case "stats-card":
      return <StatsCardSkeleton count={count} />;
    case "list":
      return <ListSkeleton count={count} />;
    case "form":
      return <FormSkeleton />;
    case "page":
      return <PageLoading />;
    case "inline":
      return <InlineLoading />;
    case "chart":
      return <ChartSkeleton />;
    default:
      return <TableRowSkeleton columns={columns} rows={rows} />;
  }
};

export default LoadingSkeleton;
